import type { ReactNode } from 'react'
import { cn } from '@saas-pages/shared/ui'
import { LocalizedProcessingBadge } from '@/shared/i18n'

interface ToolHeroProps {
  eyebrow?: ReactNode
  title: ReactNode
  description: ReactNode
  chips?: ReactNode[]
  badge?: 'local' | 'local-first'
  /** Leaves only the title on phones once a file is open, so the tool stays on screen. */
  collapsed?: boolean
  children?: ReactNode
}

export function ToolHero({
  eyebrow,
  title,
  description,
  chips = [],
  badge = 'local',
  collapsed = false,
  children,
}: ToolHeroProps) {
  return (
    <section className="pb-3 sm:pb-8 sm:pt-2 sm:text-center">
      {eyebrow && (
        <div className="mb-4 hidden rounded-md border bg-card px-2.5 py-1 text-xs font-medium text-muted-foreground shadow-sm sm:inline-flex">
          {eyebrow}
        </div>
      )}
      <h1 className="mx-auto max-w-3xl text-2xl font-bold tracking-normal sm:text-5xl">
        {title}
      </h1>
      <p
        className={cn(
          'mx-auto mt-1 line-clamp-2 max-w-2xl text-sm leading-6 text-muted-foreground sm:mt-4 sm:line-clamp-none sm:text-base sm:leading-7',
          collapsed && 'hidden sm:block',
        )}
      >
        {description}
      </p>
      <div
        className={cn(
          'mt-2 flex flex-wrap gap-2 sm:mt-6 sm:justify-center',
          collapsed && 'hidden sm:flex',
        )}
      >
        <LocalizedProcessingBadge variant={badge} />
        {chips.map((chip, index) => (
          <span
            key={index}
            className="hidden rounded-md border bg-card px-3 py-1.5 text-sm text-muted-foreground sm:inline"
          >
            {chip}
          </span>
        ))}
      </div>
      {children}
    </section>
  )
}
